import { useCallback, useEffect, useState } from "react";
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import { menuRoutes } from "../../../router/menu";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useHandleErrors from "../../../hooks/useHandleErrors";
import useStopsStatus from "../../../hooks/api-calls/useStopsStatus";
import { StopsService } from "../../../services/preventive-stops/stopsService";
import useFetch from "../../../hooks/useFetch";
import { toast } from "sonner";
import PageHeader from "../../../layout/shared/page-header";
import StatusDropdown from "../../../components/forms/StatusDropdown";
import { Button, Loader, Tooltip } from "rizzui";
import DeletePopover from "../../../components/buttons/DeletePopover";
import { setSelectedView } from "../../../redux/stopsProfileSlice";
import StopsFormModal from "../components/StopFormModal";


const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.stops.profile.info,
        permission: {
            group: 'preventive_stops',
            action: 'get',
        }
    },
    {
        label: 'Fotografias',
        path: menuRoutes.stops.profile.photos,
        permission: {
            group: 'preventive_stops_photos',
            action: 'get',
        }
    }

];

export default function StopsProfileLayout({ children, isLoading, setRefetchTab }: { children?: React.ReactNode, isLoading?: boolean, setRefetchTab?: (state:boolean) => void }) {

    // STATES

    const [ stops, setStops ] = useState<any>(null);
    const [ isOpen, setIsOpen ] = useState(false);

    // HOOKS

    const { selectedView } = useSelector((state: RootState) => state.stopsProfile);
    const { id = '' } = useParams<{ id: string }>();
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { getStopsStatusList } = useStopsStatus();
    const navigate = useNavigate();
    const service = new StopsService();


    // DATA FETCHING

    const [data, loading, error, refetch] = useFetch(
        useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getStopsById(id);
        return response.getResponseData();
    }
    , [id,]));


    // PAGE HEADER
    const pageHeader = {
        title: `Parada ${data?.stopCode || ''}`,
        breadcrumb: [
            { name: 'Administración' },
            { name: 'Paradas' },
            { name: 'Datos de la parada' },
        ],
    }

    // FUNCTIONS

    //----------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR PARADA
     * @EN DELETE STOP
     * @param id
     */
    //----------------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        const response = await (await service.deleteStop(id)).getResponseData();
        if(response.success) {
            toast.success("Parada eliminada correctamente");
            navigate(menuRoutes.stops.path);
            refetch();
        }else {
            handleErrors(response);
        }
    };
    //----------------------------------------------------------------------------------------------------------------------------


    //----------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CERRAR EL MODAL
     * @EN CLOSE THE MODAL
     */
    //----------------------------------------------------------------------------------------------------------------------------
    const onClose = () => {
        setIsOpen(false);

        setTimeout(() => {
            setStops(null);
            refetch();
        }, 150);
    }

    //----------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR DE ESTADO LA CARROÑA
     * @EN CHANGE THE STATE OF THE CARCASS
     * 
     * @param id
     * @param status
     */
    //----------------------------------------------------------------------------------------------------------------------------
    const handleChangeStatus = async (id: string, status: string) => {
        const response = await (await service.changeStatusStop(id, status)).getResponseData();
        if(response.success) {
            toast.success("Estado cambiado correctamente");
            refetch();
        }else {
            handleErrors(response);
        }
    };
    //------------------------------------------------------------------------------------------------------------------------


    // USE EFFECTS

    useEffect(() => {
        navigate(`${menuRoutes.stops.path}/${id}/profile/${selectedView}`);
    }
    , [selectedView]);

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb} className="mb-0 lg:mb-0">
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                    { userCan('edit', 'preventive_stops')&& data && (
                        <>
                            { userCan('validate', 'preventive_stops') && (
                            <div className='flex flex-col gap-2 justify-content-center text-center mt-1' key={id + data.preventiveStopsStatus?.id}>
                                    {(id && data.preventiveStopsStatus?.id) ? (
                                        <StatusDropdown
                                            entityId={id}
                                            title="¿Estas seguro de cambiar el estado de la parada?"
                                            statesOptions={getStopsStatusList()}
                                            currentState={data.preventiveStopsStatus}
                                            handleStateChange={(entityId: string, statusId: string) => handleChangeStatus(id, statusId)}
                                            hasComments={false}
                                        />
                                    ) : "N/A"}
                            </div> )}
                            <Tooltip content={'Editar parada'} placement={'top'} color={'invert'} size={'sm'}>
                                <Button variant={'outline'} color={'primary'} size={'md'} onClick={() => {
                                    setIsOpen(true);
                                    setStops(data.id);
                                }} className="bg-white">
                                    Editar
                                </Button>
                            </Tooltip>
                         </>
                    )}
                    {userCan('delete', 'preventive_stops') && (
                        <DeletePopover
                            title={`Eliminar parada`}
                            description={`¿Estás seguro de que deseas eliminar la parada?`}
                            size={'md'}
                            onDelete={() => handleDelete(id)}
                            actionIconClassName='h-10 w-10'
                        />
                    )}

                </div>
            </PageHeader>

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {isLoading ? <Loader height='60vh' /> : < Outlet />}

            <StopsFormModal stop={stops} isOpen={isOpen} onClose={onClose} refetch={refetch} />
        </>
    );
}